// Multilingual content for Villa Sućuraj — EN / HR / DE / IT

export const LANGUAGES = [
  { code: "en", label: "EN", name: "English" },
  { code: "hr", label: "HR", name: "Hrvatski" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "it", label: "IT", name: "Italiano" },
];

export const translations = {
  en: {
    nav: {
      about: "About",
      experiences: "Experiences",
      gallery: "Gallery",
      booking: "Booking",
      contact: "Contact",
    },
    hero: {
      eyebrow: "Sućuraj · Hvar · Croatia",
      title: "A stone house\nat the edge of the Adriatic.",
      subtitle:
        "A rustic family villa on the eastern tip of Hvar — pine, lavender and slow afternoons by the sea.",
      cta: "Check availability",
      scroll: "Scroll",
    },
    about: {
      eyebrow: "The story of Villa Sućuraj",
      title: "Born in an olive grove, passed down through generations.",
      paragraph1:
        "Tucked within a protected olive grove on the eastern tip of Hvar, Villa Sućuraj is a living piece of family history, passed down through generations. Built entirely by hand by a craftsman-owner, the house was raised to honor island tradition, seamlessly weaving authentic Dalmatian elements into the rugged landscape. The ancient grove shields the property in absolute seclusion — where your only neighbors are the summer crickets — yet the historic village of Sućuraj is just a five-minute drive away.",
      paragraph2:
        "Mornings here begin with coffee on the wide stone terrace, enveloped in the quiet scent of lavender and wild olive trees. Days are spent discovering sun-dappled, cobbled paths winding through pine trees and orchards, swimming along hidden beaches, and watching the Adriatic slowly fade into the night over the rich, simple flavours of a Dalmatian kitchen.",
      featuresTitle: "Features",
      features: [
        "Sleeps up to 6 guests · 2 bedrooms + Duplex",
        "Private stone terrace with olive orchard view",
        "Rustic BBQ house with traditional peka",
        "Olive grove & herb garden",
        "900 m to the nearest beach",
        "Wi-Fi · Air-con",
      ],
      locationTitle: "The location",
      locationText:
        "Sućuraj is a small fishing village at the far eastern end of Hvar — quiet, authentic, just a short ferry from the mainland at Drvenik. The south coast of the island is yours to explore: hidden coves, vineyards and centuries-old stone paths.",
      mapTitle: "Find us on the map",
      mapDirections: "Open in Google Maps",
    },
    experiences: {
      eyebrow: "Things to do",
      title: "Slow days, island flavours.",
      subtitle:
        "We can arrange any of these experiences for you during your stay. Just ask.",
      items: [
        {
          tag: "Harmony",
          title: "Harmony & Privacy",
          body: "Greet the morning on the terrace with an espresso, enveloped in the quiet scent of lavender and the view of the ancient olive orchard. Here, the day has no schedule.",
        },
        {
          tag: "Nature",
          title: "Nature & Sea",
          body: "Stroll for ten minutes down a shaded pine-forest path to a secluded rocky cove with crystal-clear water. Most days, you will have the entire Adriatic to yourself.",
        },
        {
          tag: "Explore",
          title: "Island Exploration",
          body: "Quiet coastal roads, hidden coves, and the narrow, cobbled streets of Sućuraj offer endless paths to explore on foot, by bicycle, or by boat. Tell us what you love, and we are happy to arrange an itinerary tailored entirely to your pace.",
        },
        {
          tag: "Cuisine",
          title: "Culinary Rituals",
          body: "Dalmatian mealtimes are a sacred family tradition. Indulge in slow-cooked lamb pečenje or an authentic octopus peka, prepared in the villa's stone fireplace and paired with rich Hvar wines.\n\nGuests are welcome to harvest fresh ingredients from our private vegetable and herb garden, and we are always delighted to arrange a private local chef to cook for you.",
        },
      ],
    },
    gallery: {
      eyebrow: "Pictures",
      title: "The house, in light.",
      subtitle: "A first glimpse — more on the way once we shoot the season.",
    },
    booking: {
      eyebrow: "Reserve your stay",
      title: "Check availability.",
      subtitle:
        "Pick your dates on the calendar — booked periods are crossed out. Send us a request and we will reply within 24 hours.",
      checkIn: "Check in",
      checkOut: "Check out",
      guests: "Guests",
      name: "Full name",
      email: "Email",
      phone: "Phone (optional)",
      message: "Message (optional)",
      messagePlaceholder: "Tell us a bit about your trip…",
      submit: "Send booking request",
      submitting: "Sending…",
      legendAvailable: "Available",
      legendBooked: "Booked",
      legendSelected: "Your stay",
      successTitle: "Request received",
      successBody: "Thank you — we will get back to you within 24 hours.",
      errorPickDates: "Please select a check-in and check-out date.",
      errorOverlap: "These dates overlap with an existing booking. Please choose other dates.",
      errorGeneric: "Something went wrong. Please try again.",
      nights: "nights",
      priceHeading: "Estimated total",
      priceSubtotal: "Subtotal",
      priceNightlyBase: "Nightly base",
      priceDiscount: "Weekly discount",
      priceTotal: "Total",
      pricePerNight: "per night",
      priceFrom: "from",
      priceDiscountHint: "Stay {n} nights or more and get {p}% off automatically.",
      priceDiscountApplied: "Weekly discount applied ({p}% off).",
      priceLoading: "Calculating…",
    },
    footer: {
      tagline: "A small rustic villa in Sućuraj, Hvar.",
      contact: "Contact",
      address: "Sućuraj 21469, Hvar, Croatia",
      rights: "All rights reserved.",
    },
  },

  hr: {
    nav: {
      about: "O kući",
      experiences: "Doživljaji",
      gallery: "Galerija",
      booking: "Rezervacija",
      contact: "Kontakt",
    },
    hero: {
      eyebrow: "Sućuraj · Hvar · Hrvatska",
      title: "Kamena kuća\nna rubu Jadrana.",
      subtitle:
        "Rustikalna obiteljska vila na istočnom vrhu Hvara — bor, lavanda i mirna popodneva uz more.",
      cta: "Provjeri slobodne termine",
      scroll: "Niže",
    },
    about: {
      eyebrow: "Priča o Vili Sućuraj",
      title: "Sagrađena u maslinjaku, prenesena s koljena na koljeno.",
      paragraph1:
        "Smještena u zaštićenom maslinjaku na istočnom vrhu Hvara, Villa Sućuraj živi je dio obiteljske povijesti, prenošen kroz generacije. Sagrađena u potpunosti rukom vlasnika-majstora, kuća je podignuta uz poštovanje otočne tradicije, spajajući autentične dalmatinske elemente s krševitim krajolikom. Drevni maslinjak okružuje imanje u potpunoj samoći — vaši jedini susjedi su ljetni cvrčci — a povijesno selo Sućuraj samo je pet minuta vožnje udaljeno.",
      paragraph2:
        "Jutra ovdje počinju kavom na prostranoj kamenoj terasi, u tihom mirisu lavande i divlje masline. Dani prolaze otkrivajući suncem prošarane staze pod borovima i voćnjacima, kupanjem na skrivenim plažama i pogledom na Jadran koji polako tone u noć, uz jednostavne okuse dalmatinske kuhinje.",
      featuresTitle: "Sadržaji",
      features: [
        "Do 6 gostiju · 2 spavaće sobe + duplex",
        "Privatna kamena terasa s pogledom na maslinjak",
        "Rustikalna ljetna kuhinja s tradicionalnom pekom",
        "Maslinik i začinski vrt",
        "900 m do najbliže plaže",
        "Wi-Fi · klima",
      ],
      locationTitle: "Lokacija",
      locationText:
        "Sućuraj je malo ribarsko mjesto na krajnjem istoku Hvara — mirno, autentično, samo kratka vožnja trajektom iz Drvenika. Južna obala otoka čeka da je istražite: skrivene uvale, vinogradi i stoljetni kameni putevi.",
      mapTitle: "Pronađite nas na karti",
      mapDirections: "Otvori u Google kartama",
    },
    experiences: {
      eyebrow: "Što raditi",
      title: "Spori dani, otočki okusi.",
      subtitle:
        "Možemo vam organizirati bilo koji od ovih doživljaja tijekom boravka. Samo pitajte.",
      items: [
        {
          tag: "Mir",
          title: "Sklad i privatnost",
          body: "Dočekajte jutro na terasi uz espresso, u tihom mirisu lavande i pogledu na drevni maslinjak. Ovdje dan nema raspored.",
        },
        {
          tag: "Priroda",
          title: "Priroda i more",
          body: "Hodajte deset minuta sjenovitom stazom kroz borovu šumu do skrivene stjenovite uvale s kristalno čistom vodom. Većinu dana cijeli Jadran imat ćete samo za sebe.",
        },
        {
          tag: "Istraživanje",
          title: "Otkrivanje otoka",
          body: "Mirne obalne ceste, skrivene uvale i uske kamene ulice Sućurja nude bezbroj putova za istraživanje pješice, biciklom ili brodom. Recite nam što volite, a mi ćemo rado pripremiti itinerar potpuno prilagođen vašem ritmu.",
        },
        {
          tag: "Kuhinja",
          title: "Kulinarski rituali",
          body: "Dalmatinski objedi sveta su obiteljska tradicija. Prepustite se polako pečenoj janjetini ili autentičnoj hobotnici ispod peke, pripremljenoj u kamenom ognjištu vile i poslužnoj uz bogata hvarska vina.\n\nGosti su dobrodošli ubrati svježe sastojke iz našeg privatnog povrtnjaka i začinskog vrta, a uvijek nam je zadovoljstvo organizirati lokalnog kuhara koji će kuhati za vas.",
        },
      ],
    },
    gallery: {
      eyebrow: "Slike",
      title: "Kuća u svjetlu.",
      subtitle: "Prvi uvid — slijedi više nakon sezonskog snimanja.",
    },
    booking: {
      eyebrow: "Rezervirajte boravak",
      title: "Provjerite slobodne termine.",
      subtitle:
        "Odaberite datume u kalendaru — zauzeti termini su prekriženi. Pošaljite upit i odgovaramo u 24 sata.",
      checkIn: "Dolazak",
      checkOut: "Odlazak",
      guests: "Gosti",
      name: "Ime i prezime",
      email: "E-mail",
      phone: "Telefon (nije obvezno)",
      message: "Poruka (nije obvezno)",
      messagePlaceholder: "Recite nam malo o svom putovanju…",
      submit: "Pošalji upit",
      submitting: "Šaljem…",
      legendAvailable: "Slobodno",
      legendBooked: "Zauzeto",
      legendSelected: "Vaš boravak",
      successTitle: "Upit zaprimljen",
      successBody: "Hvala — javljamo se u roku od 24 sata.",
      errorPickDates: "Odaberite datum dolaska i odlaska.",
      errorOverlap: "Odabrani datumi preklapaju se s postojećom rezervacijom. Molimo odaberite druge datume.",
      errorGeneric: "Došlo je do pogreške. Pokušajte ponovno.",
      nights: "noćenja",
      priceHeading: "Procijenjeni ukupni iznos",
      priceSubtotal: "Međuzbroj",
      priceNightlyBase: "Cijena po noći",
      priceDiscount: "Tjedni popust",
      priceTotal: "Ukupno",
      pricePerNight: "po noći",
      priceFrom: "od",
      priceDiscountHint: "Ostanite {n} ili više noćenja i automatski dobivate {p}% popusta.",
      priceDiscountApplied: "Primijenjen tjedni popust ({p}%).",
      priceLoading: "Izračunavam…",
    },
    footer: {
      tagline: "Mala rustikalna vila u Sućurju, Hvar.",
      contact: "Kontakt",
      address: "Sućuraj 21469, Hvar, Hrvatska",
      rights: "Sva prava pridržana.",
    },
  },

  de: {
    nav: {
      about: "Über das Haus",
      experiences: "Erlebnisse",
      gallery: "Galerie",
      booking: "Buchung",
      contact: "Kontakt",
    },
    hero: {
      eyebrow: "Sućuraj · Hvar · Kroatien",
      title: "Ein Steinhaus\nam Rand der Adria.",
      subtitle:
        "Eine rustikale Villa am östlichen Ende von Hvar — Pinien, Lavendel und ruhige Nachmittage am Meer.",
      cta: "Verfügbarkeit prüfen",
      scroll: "Mehr",
    },
    about: {
      eyebrow: "Die Geschichte der Villa Sućuraj",
      title: "Geboren in einem Olivenhain, weitergegeben über Generationen.",
      paragraph1:
        "Geschützt inmitten eines uralten Olivenhains an der östlichen Spitze von Hvar gelegen, ist die Villa Sućuraj ein lebendiges Stück Familiengeschichte, das über Generationen weitergegeben wurde. Vollständig von Hand durch einen Handwerker-Eigentümer errichtet, ehrt das Haus die Tradition der Insel und verwebt authentische dalmatinische Elemente mit der rauen Landschaft. Der alte Hain umgibt das Anwesen in absoluter Abgeschiedenheit — Ihre einzigen Nachbarn sind die Grillen des Sommers — und doch ist das historische Dorf Sućuraj nur fünf Autominuten entfernt.",
      paragraph2:
        "Die Morgen beginnen hier mit Kaffee auf der weiten Steinterrasse, umhüllt vom stillen Duft von Lavendel und wilden Olivenbäumen. Die Tage vergehen beim Entdecken sonnengesprenkelter Kopfsteinwege durch Pinien und Obstgärten, beim Schwimmen an versteckten Stränden und beim Blick auf die Adria, die langsam in die Nacht übergeht — begleitet von den schlichten Aromen der dalmatinischen Küche.",
      featuresTitle: "Ausstattung",
      features: [
        "Bis zu 6 Gäste · 2 Schlafzimmer + Duplex",
        "Private Steinterrasse mit Blick auf den Olivenhain",
        "Rustikales BBQ-Haus mit traditioneller Peka",
        "Olivenhain und Kräutergarten",
        "900 m zum nächsten Strand",
        "WLAN · Klimaanlage",
      ],
      locationTitle: "Die Lage",
      locationText:
        "Sućuraj ist ein kleines Fischerdorf am östlichen Ende von Hvar — ruhig, authentisch, nur eine kurze Fährfahrt vom Festland bei Drvenik entfernt. Die Südküste der Insel wartet darauf, entdeckt zu werden: versteckte Buchten, Weinberge und jahrhundertealte Steinpfade.",
      mapTitle: "Finden Sie uns auf der Karte",
      mapDirections: "In Google Maps öffnen",
    },
    experiences: {
      eyebrow: "Was tun",
      title: "Langsame Tage, Aromen der Insel.",
      subtitle:
        "Wir können jedes dieser Erlebnisse während Ihres Aufenthalts arrangieren. Fragen Sie einfach.",
      items: [
        {
          tag: "Ruhe",
          title: "Harmonie & Privatsphäre",
          body: "Begrüßen Sie den Morgen auf der Terrasse mit einem Espresso, umhüllt vom stillen Duft von Lavendel und dem Blick auf den uralten Olivenhain. Hier hat der Tag keinen Zeitplan.",
        },
        {
          tag: "Natur",
          title: "Natur & Meer",
          body: "Ein zehnminütiger Spaziergang führt über einen schattigen Pinienweg zu einer abgelegenen, felsigen Bucht mit kristallklarem Wasser. An den meisten Tagen gehört Ihnen die gesamte Adria ganz allein.",
        },
        {
          tag: "Entdecken",
          title: "Inselerkundung",
          body: "Ruhige Küstenstraßen, versteckte Buchten und die schmalen Kopfsteinpflastergassen von Sućuraj bieten unzählige Wege zum Entdecken — zu Fuß, mit dem Fahrrad oder dem Boot. Sagen Sie uns, was Sie lieben, und wir stellen Ihnen gerne ein Programm zusammen, das ganz Ihrem Tempo entspricht.",
        },
        {
          tag: "Küche",
          title: "Kulinarische Rituale",
          body: "Mahlzeiten sind in Dalmatien eine heilige Familientradition. Genießen Sie langsam gegartes Lamm-Pečenje oder authentischen Oktopus aus der Peka, zubereitet im Steinkamin der Villa und begleitet von vollmundigen Weinen aus Hvar.\n\nGäste dürfen frische Zutaten aus unserem privaten Gemüse- und Kräutergarten ernten, und wir organisieren mit Freude einen privaten lokalen Koch, der für Sie kocht.",
        },
      ],
    },
    gallery: {
      eyebrow: "Bilder",
      title: "Das Haus, im Licht.",
      subtitle: "Ein erster Eindruck — mehr folgt nach den Aufnahmen der Saison.",
    },
    booking: {
      eyebrow: "Aufenthalt reservieren",
      title: "Verfügbarkeit prüfen.",
      subtitle:
        "Wählen Sie Ihre Daten im Kalender — belegte Zeiträume sind durchgestrichen. Senden Sie uns eine Anfrage und wir antworten innerhalb von 24 Stunden.",
      checkIn: "Anreise",
      checkOut: "Abreise",
      guests: "Gäste",
      name: "Vor- und Nachname",
      email: "E-Mail",
      phone: "Telefon (optional)",
      message: "Nachricht (optional)",
      messagePlaceholder: "Erzählen Sie uns kurz von Ihrer Reise…",
      submit: "Anfrage senden",
      submitting: "Wird gesendet…",
      legendAvailable: "Frei",
      legendBooked: "Belegt",
      legendSelected: "Ihr Aufenthalt",
      successTitle: "Anfrage erhalten",
      successBody: "Vielen Dank — wir melden uns innerhalb von 24 Stunden.",
      errorPickDates: "Bitte wählen Sie ein An- und Abreisedatum.",
      errorOverlap: "Diese Daten überschneiden sich mit einer bestehenden Buchung. Bitte wählen Sie andere Daten.",
      errorGeneric: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
      nights: "Nächte",
      priceHeading: "Voraussichtlicher Gesamtpreis",
      priceSubtotal: "Zwischensumme",
      priceNightlyBase: "Preis pro Nacht",
      priceDiscount: "Wochenrabatt",
      priceTotal: "Gesamt",
      pricePerNight: "pro Nacht",
      priceFrom: "ab",
      priceDiscountHint: "Bleiben Sie {n} Nächte oder mehr und erhalten Sie automatisch {p}% Rabatt.",
      priceDiscountApplied: "Wochenrabatt angewendet ({p}% Rabatt).",
      priceLoading: "Wird berechnet…",
    },
    footer: {
      tagline: "Eine kleine rustikale Villa in Sućuraj, Hvar.",
      contact: "Kontakt",
      address: "Sućuraj 21469, Hvar, Kroatien",
      rights: "Alle Rechte vorbehalten.",
    },
  },

  it: {
    nav: {
      about: "La casa",
      experiences: "Esperienze",
      gallery: "Galleria",
      booking: "Prenotazione",
      contact: "Contatti",
    },
    hero: {
      eyebrow: "Sućuraj · Hvar · Croazia",
      title: "Una casa di pietra\nsul bordo dell'Adriatico.",
      subtitle:
        "Una villa rustica all'estremità orientale di Hvar — pini, lavanda e lunghi pomeriggi in riva al mare.",
      cta: "Verifica disponibilità",
      scroll: "Giù",
    },
    about: {
      eyebrow: "La storia di Villa Sućuraj",
      title: "Nata in un uliveto, tramandata di generazione in generazione.",
      paragraph1:
        "Custodita all'interno di un uliveto protetto all'estremità orientale di Hvar, Villa Sućuraj è un pezzo vivo di storia familiare, tramandato di generazione in generazione. Costruita interamente a mano da un artigiano-proprietario, la casa è stata edificata nel rispetto della tradizione dell'isola, intrecciando con cura autentici elementi dalmati al paesaggio aspro. L'antico uliveto avvolge la proprietà in un isolamento assoluto — i vostri unici vicini sono i grilli dell'estate — eppure lo storico villaggio di Sućuraj è a soli cinque minuti d'auto.",
      paragraph2:
        "Le mattine qui iniziano con un caffè sull'ampia terrazza in pietra, avvolti dal quieto profumo di lavanda e ulivi selvatici. Le giornate trascorrono scoprendo sentieri di pietra screziati di sole tra pini e frutteti, nuotando su spiagge nascoste, e guardando l'Adriatico spegnersi lentamente nella notte sui sapori semplici della cucina dalmata.",
      featuresTitle: "Caratteristiche",
      features: [
        "Fino a 6 ospiti · 2 camere + duplex",
        "Terrazza privata in pietra con vista uliveto",
        "Casa BBQ rustica con peka tradizionale",
        "Uliveto e orto aromatico",
        "900 m dalla spiaggia più vicina",
        "Wi-Fi · aria condizionata",
      ],
      locationTitle: "La posizione",
      locationText:
        "Sućuraj è un piccolo villaggio di pescatori all'estremità orientale di Hvar — tranquillo, autentico, a breve distanza in traghetto dalla terraferma di Drvenik. La costa meridionale dell'isola attende di essere esplorata: calette nascoste, vigneti e antichi sentieri in pietra.",
      mapTitle: "Trovaci sulla mappa",
      mapDirections: "Apri in Google Maps",
    },
    experiences: {
      eyebrow: "Cosa fare",
      title: "Giorni lenti, sapori dell'isola.",
      subtitle:
        "Possiamo organizzare ognuna di queste esperienze durante il vostro soggiorno. Basta chiedere.",
      items: [
        {
          tag: "Quiete",
          title: "Armonia e privacy",
          body: "Accogliete il mattino in terrazza con un espresso, avvolti dal quieto profumo di lavanda e dalla vista dell'antico uliveto. Qui la giornata non ha un programma.",
        },
        {
          tag: "Natura",
          title: "Natura e mare",
          body: "Una camminata di dieci minuti lungo un sentiero ombreggiato tra i pini conduce a una caletta rocciosa appartata con acqua cristallina. Quasi sempre avrete tutto l'Adriatico per voi.",
        },
        {
          tag: "Esplora",
          title: "Esplorazione dell'isola",
          body: "Strade costiere silenziose, calette nascoste e le strette vie acciottolate di Sućuraj offrono infiniti sentieri da scoprire a piedi, in bicicletta o in barca. Diteci cosa amate e saremo felici di preparare un itinerario su misura per il vostro ritmo.",
        },
        {
          tag: "Cucina",
          title: "Rituali culinari",
          body: "I pasti dalmati sono una sacra tradizione di famiglia. Concedetevi un agnello pečenje cotto lentamente o un'autentica peka di polpo, preparati nel focolare in pietra della villa e accompagnati da corposi vini di Hvar.\n\nGli ospiti sono i benvenuti a raccogliere ingredienti freschi dal nostro orto privato e dal giardino di erbe aromatiche, e siamo sempre lieti di organizzare uno chef locale che cucini per voi.",
        },
      ],
    },
    gallery: {
      eyebrow: "Immagini",
      title: "La casa, nella luce.",
      subtitle: "Un primo sguardo — seguiranno altre dopo lo shooting di stagione.",
    },
    booking: {
      eyebrow: "Prenota il tuo soggiorno",
      title: "Verifica la disponibilità.",
      subtitle:
        "Scegli le date nel calendario — i periodi prenotati sono barrati. Inviaci una richiesta e ti risponderemo entro 24 ore.",
      checkIn: "Check-in",
      checkOut: "Check-out",
      guests: "Ospiti",
      name: "Nome e cognome",
      email: "Email",
      phone: "Telefono (opzionale)",
      message: "Messaggio (opzionale)",
      messagePlaceholder: "Raccontaci brevemente del tuo viaggio…",
      submit: "Invia richiesta",
      submitting: "Invio in corso…",
      legendAvailable: "Disponibile",
      legendBooked: "Prenotato",
      legendSelected: "Il tuo soggiorno",
      successTitle: "Richiesta ricevuta",
      successBody: "Grazie — ti risponderemo entro 24 ore.",
      errorPickDates: "Seleziona una data di check-in e una di check-out.",
      errorOverlap: "Queste date si sovrappongono a una prenotazione esistente. Scegli altre date.",
      errorGeneric: "Qualcosa è andato storto. Riprova.",
      nights: "notti",
      priceHeading: "Totale stimato",
      priceSubtotal: "Subtotale",
      priceNightlyBase: "Prezzo a notte",
      priceDiscount: "Sconto settimanale",
      priceTotal: "Totale",
      pricePerNight: "a notte",
      priceFrom: "da",
      priceDiscountHint: "Restate {n} notti o più e ricevete automaticamente il {p}% di sconto.",
      priceDiscountApplied: "Sconto settimanale applicato ({p}%).",
      priceLoading: "Calcolo in corso…",
    },
    footer: {
      tagline: "Una piccola villa rustica a Sućuraj, Hvar.",
      contact: "Contatti",
      address: "Sućuraj 21469, Hvar, Croazia",
      rights: "Tutti i diritti riservati.",
    },
  },
};
;
